import React, {useState} from 'react'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import {RootState} from '../../../../setup'
import {useSelector} from 'react-redux'
const selectTimeZone = (state: RootState) => state.timeZone.timeZone

interface AssessmentRecord {
  location: string
  rating: string
  onset: 'Acute' | 'Intermittent' | 'Chronic' | ''
  reliefMeasures: 'Medication' | 'Other' | ''
  otherDetails: string
}

interface NursesAssessmentDialogProps {
  nursesAssessmentRecords: AssessmentRecord[]
  setNursesAssessmentRecords: (records: AssessmentRecord[]) => void
  open: boolean
  onOpen: () => void
  onClose: () => void
  isDisabled?: boolean
}

const NursesAssessmentDialogue: React.FC<NursesAssessmentDialogProps> = ({
  nursesAssessmentRecords,
  setNursesAssessmentRecords,
  open,
  onOpen,
  onClose,
  isDisabled = false,
}) => {
  const [newRecord, setNewRecord] = useState<AssessmentRecord>({
    location: '',
    rating: '',
    onset: '',
    reliefMeasures: '',
    otherDetails: '',
  })
  const [showWarning, setShowWarning] = useState(false)
  const timeZone = useSelector(selectTimeZone)

  const handleAddRecord = () => {
    if (!newRecord.location || !newRecord.rating || !newRecord.onset || !newRecord.reliefMeasures) {
      setShowWarning(true)
      return
    }
    setNursesAssessmentRecords([...nursesAssessmentRecords, newRecord])
    setNewRecord({
      location: '',
      rating: '',
      onset: '',
      reliefMeasures: '',
      otherDetails: '',
    })
    onClose()
  }

  const handleDeleteRecord = (index: number) => {
    const updated = nursesAssessmentRecords.filter((_, i) => i !== index)
    setNursesAssessmentRecords(updated)
  }

  const handleInputChange = (field: keyof AssessmentRecord, value: any) => {
    setNewRecord((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleCloseWarning = () => {
    setShowWarning(false)
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead style={{backgroundColor: '#2f3347'}}>
            <TableRow>
              <TableCell style={{color: 'white'}}>Actions</TableCell>
              <TableCell style={{color: 'white'}}>Location</TableCell>
              <TableCell style={{color: 'white'}}>Rating</TableCell>
              <TableCell style={{color: 'white'}}>Onset/Duration</TableCell>
              <TableCell style={{color: 'white'}}>Relief</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {nursesAssessmentRecords.length > 0 ? (
              nursesAssessmentRecords.map((record, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <IconButton 
                      onClick={() => handleDeleteRecord(index)}
                      disabled={isDisabled}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>{record.location}</TableCell>
                  <TableCell>{record.rating}</TableCell>
                  <TableCell>{record.onset}</TableCell>
                  <TableCell>
                    {record.reliefMeasures}
                    {record.reliefMeasures === 'Other' && record.otherDetails ? (
                      <div style={{fontSize: '0.85em', color: '#555'}}>{record.otherDetails}</div>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={5}
                  align='center'
                  style={{fontWeight: 'bold', fontSize: '16px'}}
                >
                  No Records Added
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Button 
        variant='contained' 
        onClick={onOpen} 
        startIcon={<AddIcon />} 
        sx={{mt: 2}}
        disabled={isDisabled}
      >
        Add Pain Assessment
      </Button>

      <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle>Add Pain Assessment</DialogTitle>
        <DialogContent sx={{pt: 2}}>
          <TextField
            label='Location'
            fullWidth
            value={newRecord.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            sx={{mb: 2, mt: 1}}
          />
          <TextField
            label='Rating'
            fullWidth
            value={newRecord.rating}
            onChange={(e) => handleInputChange('rating', e.target.value)}
            sx={{mb: 2}}
          />
          <FormControl fullWidth sx={{mb: 2}}>
            <InputLabel id='onset-duration-label'>Onset/Duration</InputLabel>
            <Select
              labelId='onset-duration-label'
              value={newRecord.onset}
              label='Onset/Duration'
              onChange={(e) => handleInputChange('onset', e.target.value)}
            >
              <MenuItem value='Acute'>Acute</MenuItem>
              <MenuItem value='Intermittent'>Intermittent</MenuItem>
              <MenuItem value='Chronic'>Chronic</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{mb: 2}}>
            <InputLabel id='relief-label'>Relief</InputLabel>
            <Select
              labelId='relief-label'
              value={newRecord.reliefMeasures}
              label='Relief'
              onChange={(e) => handleInputChange('reliefMeasures', e.target.value)}
            >
              <MenuItem value='Medication'>Medication</MenuItem>
              <MenuItem value='Other'>Other</MenuItem>
            </Select>
          </FormControl>
          {newRecord.reliefMeasures === 'Other' && (
            <TextField
              label='Other Details'
              fullWidth
              value={newRecord.otherDetails}
              onChange={(e) => handleInputChange('otherDetails', e.target.value)}
              multiline
              rows={3}
              sx={{mb: 2}}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant='contained' onClick={handleAddRecord}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showWarning}
        autoHideDuration={4000}
        onClose={handleCloseWarning}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
      >
        <Alert onClose={handleCloseWarning} severity='warning' sx={{width: '100%'}}>
          Please fill in all required fields (Location, Rating, Onset/Duration, Relief).
        </Alert>
      </Snackbar>
    </>
  )
}

export default NursesAssessmentDialogue
